import { useEffect, useRef, useState } from "react";
import { getLatestEthBps, subscribeEthBps } from "./useEthBpsSeries";

const WINDOW_MS = 30_000;
/** Same cadence as freq sparkline — chart advances; MQTT/WS only refreshes held TX/RX. */
export const ETH_BPS_SPARKLINE_TICK_MS = 50;
const MAX_FRAMES = Math.ceil(WINDOW_MS / ETH_BPS_SPARKLINE_TICK_MS) + 20;

export type EthBpsSparklineFrame = {
  ts_ms: number;
  tx_Mbps: number;
  rx_Mbps: number;
};

export type EthBpsSparklineState = {
  frames: EthBpsSparklineFrame[];
  liveTx: number | null;
  liveRx: number | null;
};

function trimWindow(frames: EthBpsSparklineFrame[]): EthBpsSparklineFrame[] {
  if (!frames.length) return [];
  const windowEnd = frames[frames.length - 1]!.ts_ms;
  const cutoff = windowEnd - WINDOW_MS;
  return frames.filter((f) => f.ts_ms >= cutoff).slice(-MAX_FRAMES);
}

/**
 * Rolling 30s en0 TX/RX sparkline — sample-and-hold with 50ms clock,
 * matching useFreqSparkline timing (longer window so it moves slower).
 */
export function useEthBpsSparkline(active: boolean): EthBpsSparklineState {
  const [frames, setFrames] = useState<EthBpsSparklineFrame[]>([]);
  const [liveTx, setLiveTx] = useState<number | null>(null);
  const [liveRx, setLiveRx] = useState<number | null>(null);
  const heldRef = useRef<{ tx: number | null; rx: number | null }>({ tx: null, rx: null });
  const framesRef = useRef<EthBpsSparklineFrame[]>([]);

  useEffect(() => {
    if (!active) return;

    const syncHeld = () => {
      const latest = getLatestEthBps();
      if (!latest) return;
      heldRef.current = { tx: latest.tx_Mbps, rx: latest.rx_Mbps };
      setLiveTx(latest.tx_Mbps);
      setLiveRx(latest.rx_Mbps);
    };

    syncHeld();
    return subscribeEthBps(syncHeld);
  }, [active]);

  useEffect(() => {
    if (!active) {
      heldRef.current = { tx: null, rx: null };
      framesRef.current = [];
      setFrames([]);
      setLiveTx(null);
      setLiveRx(null);
      return;
    }

    const tick = () => {
      const held = heldRef.current;
      if (held.tx == null || held.rx == null) return;

      const now = Date.now();
      const prev = framesRef.current[framesRef.current.length - 1];
      if (prev && now <= prev.ts_ms) return;

      framesRef.current = trimWindow([
        ...framesRef.current,
        { ts_ms: now, tx_Mbps: held.tx, rx_Mbps: held.rx },
      ]);
      setFrames(framesRef.current);
    };

    const intervalId = setInterval(tick, ETH_BPS_SPARKLINE_TICK_MS);
    return () => clearInterval(intervalId);
  }, [active]);

  return { frames, liveTx, liveRx };
}
